import React, { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Box,
  Dialog,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import 'react-quill/dist/quill.snow.css';
import { contentTextLooksRtl, prepareContentRichPreviewHtml } from '../../utils/contentRichPreviewHtml';

export interface ContentRichPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  /** Raw editor HTML from Quill (or HTML string from server); not Delta JSON for display. */
  html: string;
  title?: string;
}

const ContentRichPreviewDialog: React.FC<ContentRichPreviewDialogProps> = ({
  open,
  onClose,
  html,
  title = 'Content preview',
}) => {
  const [fullScreen, setFullScreen] = useState(false);

  useEffect(() => {
    if (!open) setFullScreen(false);
  }, [open]);

  const safeHtml = prepareContentRichPreviewHtml(html);
  const previewIsRtl = useMemo(() => contentTextLooksRtl(html), [html]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="xl"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: fullScreen
          ? { display: 'flex', flexDirection: 'column', height: '100%', m: 0, maxHeight: '100%' }
          : {
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'min(92vh, 920px)',
              width: '100%',
              m: { xs: 0.5, sm: 1 },
            },
      }}
    >
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar variant="dense" sx={{ gap: 0.5, minHeight: 48, px: { xs: 1, sm: 1.5 } }}>
          <Typography variant="subtitle1" component="div" sx={{ flexGrow: 1, minWidth: 0, fontWeight: 600 }} noWrap>
            {title}
          </Typography>
          <IconButton
            color="inherit"
            onClick={() => setFullScreen((v) => !v)}
            aria-label={fullScreen ? 'Exit full screen' : 'Full screen'}
          >
            {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="Close preview">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          p: { xs: 1, sm: 1.25 },
          bgcolor: 'background.default',
        }}
      >
        <Box
          className="ql-snow"
          sx={{
            maxWidth: '100%',
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: (theme) => theme.shadows[1],
            border: (theme) => `1px solid ${theme.palette.divider}`,
            '& .ql-container.ql-snow': { border: 'none' },
            '& .ql-editor': {
              minHeight: fullScreen ? 'calc(100dvh - 120px)' : '8rem',
              fontSize: '1rem',
              lineHeight: 1.6,
              ...(previewIsRtl
                ? { direction: 'rtl', textAlign: 'right !important' }
                : { direction: 'ltr' }),
              p: { xs: 1.25, sm: 1.75 },
            },
            ...(previewIsRtl
              ? {
                  '& .ql-editor p:not(.ql-align-center), & .ql-editor li:not(.ql-align-center), & .ql-editor .ql-align-left':
                    { textAlign: 'right !important' },
                }
              : {}),
            '& .ql-editor .ql-align-right': { textAlign: 'right' },
            '& .ql-editor .ql-align-center': { textAlign: 'center' },
            ...(!previewIsRtl ? { '& .ql-editor .ql-align-left': { textAlign: 'left' } } : {}),
            '& .ql-editor img, & .ql-editor video, & .ql-editor audio': {
              maxWidth: '100%',
              height: 'auto',
            },
          }}
        >
          <Box className="ql-container ql-snow" sx={{ border: 'none' }}>
            <Box
              component="div"
              className="ql-editor"
              dir={previewIsRtl ? 'rtl' : undefined}
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ContentRichPreviewDialog;
